import { testSessionStatus } from '../../lib/testSessionStatus'
import { useEffect, useId, useState } from 'react'
import {
  Bug as BugIcon,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleX,
  ExternalLink,
  Pause,
  Route as RouteIcon,
} from 'lucide-react'
import Spinner from '../Spinner'
import StatusIndicator from '../StatusIndicator'
import type { TestSession, TestSessionJourneyRun } from '../../data/projectTypes'
import { getTestRunStoryMetrics, isPendingJourneyRun } from '../../lib/journeyState'
import { elapsedSeconds, liveRunLine } from '../../lib/elapsed'
import type { ProjectPause } from '../../lib/projectPause'
import './TestRunCards.css'

// Test Run entries live directly in the project transcript. Each run is its own top-level accordion so
// progress and the remaining queue are visible without nesting one interactive card inside another.

function isLive(session: TestSession): boolean {
  return session.status === 'in-progress'
}

// Wall-clock now, re-ticked every second while the card is live. The live line below ticks against it;
// nothing re-renders on its own when a poll returns unchanged data, so without this the elapsed time
// would freeze between state changes — the same staleness the line exists to fix.
function useTickingNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    setNow(Date.now())
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(tick)
  }, [active])
  return now
}

function RunCounts({ runs }: { runs: TestSession[] }) {
  const journeyRuns = runs.flatMap(run => run.journeyRuns)
  const metrics = getTestRunStoryMetrics(journeyRuns)
  const bugs = runs.reduce((count, run) => count + run.bugs.length, 0)
  return (
    <span className="pchat-run__meta">
      <span className="pchat-run__count pchat-run__count--progress" title="Journeys finished">
        <RouteIcon size={11} aria-hidden />
        {metrics.finished} / {metrics.total} journeys
      </span>
      <span className="pchat-run__count pchat-run__count--pass" title="Passed journeys">
        <CheckCircle2 size={11} aria-hidden /> {metrics.passes}
      </span>
      <span className="pchat-run__count pchat-run__count--failure" title="Failed journeys">
        <CircleX size={11} aria-hidden /> {metrics.failures}
      </span>
      <span className="pchat-run__count pchat-run__count--bug" title="Bugs found">
        <BugIcon size={11} aria-hidden /> {bugs}
      </span>
    </span>
  )
}

function PendingJourney({ run, position, pause }: { run: TestSessionJourneyRun; position: number | null; pause: ProjectPause | null }) {
  const label = run.journey_name ?? run.goal ?? 'Untitled journey'
  return (
    <li className="pchat-run__queue-row">
      <span className="pchat-run__queue-position" aria-label={position === null ? 'Running now' : `Queue position ${position}`}>
        {position === null ? (pause ? <Pause size={12} aria-hidden /> : <Spinner size={12} />) : position}
      </span>
      <span className="pchat-run__queue-name">{label}</span>
      <StatusIndicator status={run.status} pause={pause} withIcon />
    </li>
  )
}

function TestRunCard({ session, onOpen, pause }: { session: TestSession; onOpen: (session: TestSession) => void; pause: ProjectPause | null }) {
  const [open, setOpen] = useState(false)
  const contentId = useId()
  const live = isLive(session)
  const now = useTickingNow(live)
  const pending = session.journeyRuns.filter(run => isPendingJourneyRun(run.status))
  const running = pending.filter(run => run.status === 'in-progress')
  const queued = pending.filter(run => run.status === 'queued')
  // The counts above only move when a journey reaches a terminal state, so a journey that takes minutes
  // rendered the collapsed card as frozen — indistinguishable from a dead run (bug-mu2p97vx-7abw).
  // Name what is in flight and tick the round's age; both come from the already-polled session payload.
  const liveLine = live
    ? liveRunLine({
      runningLabels: running.map(run => run.journey_name ?? run.goal ?? 'Untitled journey'),
      queuedCount: queued.length,
      elapsedSeconds: elapsedSeconds(session.started_at, now),
    })
    : null

  return (
    <div className={`pchat-run${live ? pause ? ' pchat-run--paused' : ' pchat-run--live' : ''}`} data-testid="project-chat-test-run">
      <button
        type="button"
        className="pchat-run__toggle"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(current => !current)}
        data-testid="project-chat-test-run-toggle"
        data-analytics-event="chat-test-run-toggle"
      >
        <span className="pchat-run__chevron" aria-hidden>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <span className="pchat-run__summary">
          <span className="pchat-run__row">
            <span className="pchat-run__title">{session.title}</span>
            <StatusIndicator status={testSessionStatus(session)} pause={pause} withIcon />
          </span>
          <RunCounts runs={[session]} />
          {liveLine && (
            <span className="pchat-run__live-line" data-testid="project-chat-test-run-live" title="What QA is running right now and for how long">
              {liveLine}
            </span>
          )}
        </span>
      </button>
      {open && (
        <div id={contentId} className="pchat-run__details">
          <div className="pchat-run__queue-heading">
            <span>{pending.length === 0 ? 'Journey queue' : `${pending.length} remaining`}</span>
          </div>
          {pending.length > 0 ? (
            <ul className="pchat-run__queue">
              {running.map(run => <PendingJourney key={run.id} run={run} position={null} pause={pause} />)}
              {queued.map((run, index) => <PendingJourney key={run.id} run={run} position={index + 1} pause={pause} />)}
            </ul>
          ) : (
            <p className="pchat-run__empty">All journeys in this Test Run have finished.</p>
          )}
          <button
            type="button"
            className="pchat-run__open"
            onClick={() => onOpen(session)}
            data-analytics-event="chat-test-run-open"
          >
            Open full Test Run <ExternalLink size={12} aria-hidden />
          </button>
        </div>
      )}
    </div>
  )
}

export default function TestRunEntry({ runs, onOpen, pause = null }: {
  runs: TestSession[]
  onOpen: (session: TestSession) => void
  pause?: ProjectPause | null
}) {
  return (
    <div className="pchat-run-list" data-testid="project-chat-test-run-list">
      {runs.map(run => <TestRunCard key={run.id} session={run} onOpen={onOpen} pause={pause} />)}
    </div>
  )
}
